import React, { useContext, useEffect, useState } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import styled, { css } from 'styled-components'
import ReactFullpage from '@fullpage/react-fullpage'
import { rgba, stripUnit } from 'polished'

import CheckoutContext from 'contexts/Checkout'
import NativeAuthContext from 'components/NativeAuth'
import { SLIDESHOW_SPEED } from 'utils/constants'
import { availableForSale } from 'utils/products'
import { replaceRoute } from 'lib/routing'
import useDigitalData from 'hooks/useDigitalData'
import { getVariantAttributes } from 'utils/tracking'

import { white, black, greyDark } from 'styles/colors'
import * as spacing from 'styles/spacing'
import { H1, H4, Lead, lineHeights, fontSizes, fontWeights } from 'styles/typography'
import { media, mediaDown } from 'styles/media'
import { cover, navHeight, navHeightMobile, borderRadius, animationTime, animationCurve } from 'styles/global'

import { ScrollConsumer } from 'components/ScrollContext'
import SlideshowMenu from 'components/slideshow/SlideshowMenu'
import Portal from 'components/Portal'
import Header from 'components/Header'
import HealthWarning from 'components/HealthWarning'
import Details from 'components/slideshow/Details'
import Button from 'components/button/Button'
import SwatchList from 'components/slideshow/SwatchList'
import FlavourList from 'components/slideshow/FlavourList'
import Tooltip from 'components/Tooltip'

const Container = styled.div`
  position: static;
  background-color: ${black};
`

const Section = styled.div`
  position: relative;
`

const FixedHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: ${navHeight};
  transition: opacity ${SLIDESHOW_SPEED} ease-in-out, visibility ${SLIDESHOW_SPEED} ease-in-out;
  z-index: 100;

  ${mediaDown.medium`
    display: none;
  `};

  .fp-viewing-step-1 & {
    opacity: 0;
    visibility: hidden;
  }
`

const MenuWrap = styled.div`
  transition: opacity ${SLIDESHOW_SPEED} ease-in-out, visibility ${SLIDESHOW_SPEED} ease-in-out;

  .fp-viewing-step-1 & {
    opacity: 0;
    visibility: hidden;
  }
`

const Subheading = styled(Lead)`
  font-weight: ${fontWeights.medium};
  color: ${props => (props.isDark ? white : black)} !important;
`

const Eyebrow = styled.span`
  text-transform: uppercase;
  font-size: ${fontSizes.tiny};
  display: block;
  margin-bottom: ${spacing.small};
  color: ${props => (props.isDark ? white : black)};

  ${media.small`
    font-size: ${fontSizes.small};
    margin-bottom: ${spacing.medium};
  `};

  ${media.medium`
    font-size: ${fontSizes.regular};
    margin-bottom: ${spacing.large};
  `};

  ${media.large`
    font-size: 1rem;
  `};
`

const BackButton = styled.button`
  position: fixed;
  top: 0%;
  right: 0;
  color: ${white};
  transition: opacity ${SLIDESHOW_SPEED} ease-in-out, visibility ${SLIDESHOW_SPEED} ease-in-out;
  z-index: 60;
  height: ${navHeightMobile};
  display: flex;
  align-items: center;
  padding-left: ${spacing.large};
  padding-right: ${spacing.large};
  text-transform: uppercase;
  letter-spacing: 0.1em;

  ${media.medium`
    display: none;
  `};

  .fp-viewing-step-1 & {
    opacity: 0;
    visibility: hidden;
  }
`

const Content = styled.div`
  position: relative;
  z-index: 30;
  /* pointer-events: none; */

  ${mediaDown.medium`
    text-align: center;
  `};

  p {
    color: ${props => (props.isDark ? rgba(white, 0.75) : rgba(greyDark, 0.75))};
  }
`

const Background = styled.div`
  transition: all ${SLIDESHOW_SPEED} ease-in-out;
  background-size: cover;
  background-position: center;
  ${cover('absolute')};
`

const Heading = styled(H1)`
  color: ${white};
  font-family: 'Trim Poster';
  font-size: 32px;

  ${media.small`
    font-size: 36px;
  `};

  ${media.medium`
    font-size: 40px;
  `};

  ${media.large`
    font-size: 44px;
  `};

  ${media.xLarge`
    font-size: 48px;
  `};
`

const Step1HeaderWrap = styled.div`
  position: relative;
  height: ${navHeightMobile};

  ${media.medium`
    height: ${navHeight};
  `};
`

const Step1Content = styled(Content)`
  width: 100vw;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${mediaDown.medium`
    padding: 0 7.5%;
  `};
`

const Step1Shard = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const Step1Heading = styled(H1)`
  color: ${white};
  text-transform: uppercase;
  font-family: 'Trim Poster';
  font-size: 32px;

  ${media.small`
    font-size: 39px;
  `};

  ${media.medium`
    font-size: 46px;
  `};

  ${media.large`
    font-size: 53px;
  `};

  ${media.xLarge`
    font-size: 60px;
  `};
`

const Step1Subheading = styled(H4)`
  color: ${white};
  line-height: ${lineHeights.base};
  font-weight: ${fontWeights.medium};
  margin-bottom: ${spacing.medium};

  span {
    white-space: nowrap;
  }

  ${media.small`
    margin-bottom: ${spacing.large};
  `};

  ${media.medium`
    margin-bottom: ${spacing.xLarge};
    padding-right: 10%;
  `};

  ${media.xLarge`
    margin-bottom: ${spacing.xxLarge};
  `};
`

const Step1Inner = styled.div`
  width: 100%;

  ${mediaDown.medium`
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
  `};

  ${media.medium`
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: flex-end;
    max-width: 1300px;
    padding-left: ${stripUnit(spacing.xLarge) * 1.5 + 'px'};
    padding-right: ${stripUnit(spacing.xLarge) * 1.5 + 'px'};
  `};
`

const Step1Figure = styled.div`
  margin-bottom: 0;
  max-width: 240px;

  ${mediaDown.medium`
    margin-left: auto;
    margin-right: auto;
  `};

  ${media.small`
    margin-bottom: ${spacing.medium};
    max-width: 360px;
  `};

  ${media.medium`
    margin-bottom: 0;
    max-width: 460px;
    margin-right: -16px;
  `};

  ${media.large`
    max-width: 540px;
    margin-right: 0;
  `};

  ${media.xLarge`
    max-width: 570px;
  `};
`

const Step1Main = styled.main`
  flex-shrink: 0;
  pointer-events: auto;

  ${media.medium`
    max-width: 500px;
    padding-right: 6vw;
    padding-bottom: 10vh;
  `};

  ${media.large`
    padding-right: 0;
    max-width: 460px;
  `};

  ${media.large`
    max-width: 620px;
  `};
`

const Step2DeviceWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 42.5%;
  width: 6.25vw;
  min-width: 72px;
  max-width: 108px;
  transform: translate3d(-50%, -50%, 0);
  transition: all ${SLIDESHOW_SPEED} ease-in-out;
  z-index: 10;
  opacity: 0;
  visibility: hidden;

  ${media.large`
    left: 40%;
  `};

  .fp-viewing-step-2 & {
    opacity: 1;
    visibility: visible;
  }

  img {
    width: 100%;
  }
`

const Step2SwatchWrap = styled.div`
  display: flex;
  justify-content: center;
  transition: all ${SLIDESHOW_SPEED} ease-in-out;
  opacity: 0;
  visibility: hidden;
  z-index: 30;
  margin-top: ${spacing.large};
  margin-bottom: ${spacing.large};

  ${media.small`
    margin-top: 56px;
    margin-top: ${spacing.xLarge};
    margin-bottom: ${spacing.xLarge};
  `};

  ${media.medium`
    margin-top: 0;
    margin-bottom: ${spacing.xxxLarge};
    justify-content: flex-start;
  `};

  ${media.large`
    margin-bottom: 56px;
  `};

  ${media.xLarge`
    margin-bottom: 64px;
  `};

  .fp-viewing-step-2 & {
    opacity: 1;
    visibility: visible;
  }
`

const Step2Content = styled(Content)`
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 30;
  height: 100%;

  ${mediaDown.medium`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0 10%;
  `};

  ${media.medium`
    top: 50%;
    width: 25%;
    height: auto;
    left: 62.5%;
    transform: translateY(-50%);
    max-width: 420px;
    position: absolute;
  `};

  ${media.large`
    left: 57.5%;
  `};
`

const Step2Background = styled(Background)`
  position: absolute;
`

const Step2MobileDevice = styled.img`
  margin-left: auto;
  margin-right: auto;
  width: 48px;

  ${media.small`
    width: 62px;
  `};
`

const Step2Heading = styled(Heading)`
  margin-bottom: ${spacing.medium};

  span {
    text-transform: uppercase;
  }

  ${media.small`
    margin-bottom: ${spacing.large};
  `};

  ${media.medium`
    margin-bottom: ${spacing.xxLarge};
  `};

  ${media.large`
    margin-bottom: ${spacing.xxxLarge};
    white-space: nowrap;
  `};

  ${media.xLarge`
    margin-bottom: 56px;
  `};
`

const Step3Content = styled(Content)`
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  ${mediaDown.medium`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 10%;
  `};

  ${media.medium`
    position: absolute;
    top: 50%;
    width: 25%;
    height: auto;
    left: 62.5%;
    transform: translateY(-50%);
    max-width: 420px;
    z-index: 30;
  `};

  ${media.large`
    left: 57.5%;
    max-width: 460px;
  `};

  .fp-viewing-step-3 & {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
`

const Step3FlavourWrap = styled.div`
  width: 100%; /* Take up all horizontal space */
  margin-bottom: ${spacing.medium};
  max-width: 280px;

  ${media.small`
    max-width: 320px;
    margin-bottom: ${spacing.large};
  `};

  ${mediaDown.medium`
    margin-left: auto;
    margin-right: auto;
  `};

  ${media.medium`
    max-width: 380px;
    margin-bottom: ${spacing.xLarge};
  `};

  ${media.xLarge`
    margin-bottom: ${spacing.xxLarge};
  `};
`

const Step3MobileFigure = styled.div`
  position: relative;
  display: block;
  max-width: 120px;
  margin-top: ${spacing.small};
  margin-bottom: ${spacing.medium};
  margin-left: auto;
  margin-right: auto;

  ${media.small`
    max-width: 140px;
    margin-top: ${spacing.large};
    margin-bottom: ${spacing.xLarge};
  `};
`

const Step3Figure = styled.div`
  position: fixed;
  top: 50%;
  left: 42.5%;
  width: 19vw;
  min-width: 200px;
  max-width: 340px;
  transform: translate3d(-50%, -50%, 0);
  transition: all ${SLIDESHOW_SPEED} ease-in-out;
  z-index: 10;
  opacity: 0;
  visibility: hidden;

  .fp-viewing-step-3 & {
    opacity: 1;
    visibility: visible;
  }

  ${media.large`
    left: 40%;
  `};
`

const Step3Heading = styled(Heading)`
  position: static;

  ${media.large`
    white-space: nowrap;
  `};
`

const Step3Subheading = styled.div`
  color: ${props => (props.isDark ? white : black)};
  margin-bottom: 1.25em;

  ${media.medium`
    margin-bottom: 1.5em;
  `};

  ${media.xLarge`
    height: 56px;
  `};
`

const Step3Background = styled(Background)`
  background: linear-gradient(126.91deg, #5d9892 -7.61%, #0b3c37 115.55%), #073833;
`

const Step4Content = styled(Content)`
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0 ${spacing.large};

  ${mediaDown.medium`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `};

  ${media.small`
    padding: 0 10%;
  `};

  ${media.medium`
    position: absolute;
    padding: 0;
    top: 50%;
    width: 25%;
    height: auto;
    left: 62.5%;
    transform: translateY(-50%);
    max-width: 420px;
    min-width: 320px;
  `};

  ${media.large`
    left: 57.5%;
    width: 100%;
  `};

  ${media.xLarge`
    max-width: 500px;
  `};

  .fp-viewing-step-4 & {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
`

const Step4Figure = styled.div`
  position: fixed;
  top: 50%;
  left: 42.5%;
  width: 19vw;
  min-width: 200px;
  max-width: 340px;
  transform: translate3d(-50%, -50%, 0);
  transition: all ${SLIDESHOW_SPEED} ease-in-out;
  z-index: 10;
  opacity: 0;
  visibility: hidden;

  img:last-child {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate3d(-50%, -50%, 0) scale(0.8);
    height: 130%;
    transition: all ${SLIDESHOW_SPEED} ease-in-out;

    .fp-viewing-step-4 & {
      transform: translate3d(-50%, -50%, 0);
    }
  }

  .fp-viewing-step-4 & {
    opacity: 1;
    visibility: visible;
  }

  ${media.large`
    left: 40%;
  `};
`

const Step4MobileFigure = styled.div`
  position: relative;
  display: block;
  margin-top: 32px;
  margin-bottom: 40px;
  max-width: 170px;
  margin-left: auto;
  margin-right: auto;

  img:last-child {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
    height: 130%;
  }

  ${media.small`
    max-width: 220px;
    margin-top: 44px;
    margin-bottom: 56px;
  `};
`

const Step4Disclaimer = styled.p`
  color: ${white} !important;
  font-size: 10px;
  margin-top: ${spacing.large};

  ${media.xLarge`
    margin-top: ${spacing.xLarge};
  `};

  ${mediaDown.medium`
    display: none;
  `};
`

const Step4Subheading = styled(Subheading)`
  margin-bottom: ${spacing.small};

  ${media.medium`
    margin-bottom: 1em;
  `};
`

const Strengths = styled.div`
  display: flex;
  margin-bottom: ${spacing.large};
  ${media.large`
    width: 420px;
    margin-bottom: ${spacing.xLarge};
  `};
`

const StrengthsInner = styled.div`
  display: flex;
  justify-content: center;
  margin: -${stripUnit(spacing.tiny) * 1.5 + 'px'};
  ${media.medium`
    justify-content: flex-start;
    margin: -${spacing.small};
  `};
  ${media.large`
    flex-wrap: wrap;
  `};
`

const Strength = styled.div`
  cursor: pointer;
  border: 1px solid ${white};
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.medium};
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${white};
  border-radius: ${borderRadius};
  padding: ${spacing.small} ${stripUnit(spacing.small) * 1.5 + 'px'};
  text-align: center;
  transition: all ${animationTime} ${animationCurve};
  margin: ${stripUnit(spacing.tiny) * 1.5 + 'px'};

  ${props =>
    props.selected &&
    css`
      background-color: ${white};
      color: ${black};
    `}

  ${props =>
    props.disabled &&
    css`
      opacity: 0.3;
      pointer-events: none;
    `}

  ${media.medium`
    margin: ${spacing.small};
  `};

  ${media.large`
    min-width: 70px;
    padding: ${stripUnit(spacing.small) * 1.5 + 'px'} ${spacing.medium};
  `};
`

const COLORS = [
  {
    color: 'linear-gradient(90deg, #000000 12.5%, #3A3A3A 95.83%)',
    name: 'black',
    prettyName: 'Black',
    shopifyTitle: 'ePod 2 - Black',
    isDark: true,
    active: true,
    background: 'linear-gradient(72.46deg, #010101 0%, #393939 86.51%)'
  },
  {
    color: 'linear-gradient(90deg, #61666F 12.5%, #D4D7DF 95.83%)',
    name: 'silver',
    prettyName: 'Silver',
    shopifyTitle: 'ePod 2 - Silver',
    isDark: true,
    active: false,
    background: 'linear-gradient(72.46deg, #4D4D4D 0%, #979797 86.51%)'
  },
  {
    color: 'linear-gradient(90deg, #625251 12.5%, #E7B8B0 95.83%)',
    name: 'rose-gold',
    prettyName: 'Rose Gold',
    shopifyTitle: 'ePod 2 - Rose Gold',
    isDark: true,
    active: false,
    background: 'linear-gradient(60.65deg, #c88076 0%, #e59d93 86.51%)'
  },
  {
    color: 'linear-gradient(90deg, #746B5C 12.5%, #D6BE9C 95.83%)',
    name: 'gold',
    prettyName: 'Gold',
    shopifyTitle: 'ePod 2 - Gold',
    isDark: true,
    active: false,
    background: 'linear-gradient(60.65deg, #bd893f 0%, #ebbc57 86.51%)'
  },
  {
    color: 'linear-gradient(90deg, #4D4D4D 12.5%, #989898 95.83%)',
    name: 'graphite',
    prettyName: 'Graphite',
    shopifyTitle: 'ePod 2 - Graphite',
    isDark: true,
    active: false,
    background: 'linear-gradient(60.65deg, #343834 0%, #6F746F 86.51%)'
  }
]

const FLAVOURS = [
  {
    name: 'cool-peppermint',
    prettyName: 'Cool Peppermint',
    shopifyTitle: 'Cool Peppermint',
    description: 'A balanced cooling peppermint taste.',
    isDark: true,
    active: false,
    background: 'linear-gradient(113.23deg, #008066 -14.41%, #004031 107.54%)'
  },
  {
    name: 'rich-tobacco',
    prettyName: 'Rich Tobacco',
    shopifyTitle: 'Rich Tobacco',
    description: 'Well-balanced with a smooth taste.',
    isDark: true,
    active: false,
    background: 'linear-gradient(114.13deg, #A0784E -22.45%, #3E271E 108.62%)'
  },
  {
    name: 'strawberry',
    prettyName: 'Strawberry',
    shopifyTitle: 'Strawberry',
    description: 'A fusion of strawberry with green fruit notes.',
    isDark: true,
    active: false,
    background: 'linear-gradient(110.85deg, #DF525C -7.61%, #A4101C 115.55%)'
  },
  {
    name: 'blood-orange',
    prettyName: 'Blood Orange',
    shopifyTitle: 'Blood Orange',
    description: 'An orange flavour with red pomegranate notes.',
    isDark: true,
    active: false,
    background: 'linear-gradient(110.85deg, #EC6A4C -7.61%, #B33C36 115.55%)'
  },
  {
    name: 'blueberry',
    prettyName: 'Blueberry',
    shopifyTitle: 'Blueberry',
    description: 'A blueberry flavour with undertones of red berries and tartness.',
    isDark: true,
    active: false,
    background: 'linear-gradient(116.87deg, #A296C9 -7.61%, #4D4384 113.2%)'
  },
  {
    name: 'watermelon',
    prettyName: 'Watermelon',
    shopifyTitle: 'Watermelon',
    description: 'A watermelon flavour, with a blend of subtle green notes.',
    isDark: true,
    active: true,
    background: 'linear-gradient(116.87deg, #D14441 -7.61%, #F19285 115.55%)'
  },
  {
    name: 'lychee',
    prettyName: 'Lychee',
    shopifyTitle: 'Lychee',
    description: 'A combination of lychee and mango notes.',
    isDark: true,
    active: false,
    background: 'linear-gradient(110.85deg, #C13930 -7.61%, #B33C36 115.55%)'
  },
  {
    name: 'passionfruit',
    prettyName: 'Passionfruit',
    shopifyTitle: 'Passionfruit',
    description: 'Tropical passionfruit with almond notes.',
    isDark: true,
    active: false,
    background: 'linear-gradient(110.85deg, #7B2783 -7.61%, #551E53 115.55%)'
  },
  {
    name: 'cucumber',
    prettyName: 'Cucumber',
    shopifyTitle: 'Cucumber',
    description: 'Cucumber freshness with an element of tanginess.',
    isDark: true,
    active: false,
    background: 'linear-gradient(110.85deg, #589B75 -7.61%, #023A1A 113.2%)'
  },
  {
    name: 'mango',
    prettyName: 'Mango',
    shopifyTitle: 'Mango',
    description: 'A rich mango flavour immersed with fruity notes.',
    isDark: true,
    active: false,
    background: 'linear-gradient(110.85deg, #DBA700 -7.61%, #9C6E00 115.55%)'
  },
  {
    name: 'tobacco',
    prettyName: 'Golden Tobacco',
    shopifyTitle: 'Golden Tobacco*',
    description: 'A well balanced intensity with a smooth taste.',
    isDark: true,
    active: false,
    background: 'linear-gradient(110.85deg, #D18F0E -7.61%, #543000 115.55%)'
  },
  {
    name: 'vanilla',
    prettyName: 'Vanilla',
    shopifyTitle: 'Vanilla',
    description: 'A rich vanilla taste with a smooth texture.',
    isDark: true,
    active: false,
    background: 'linear-gradient(110.85deg, #CA9957 -7.61%, #725628 113.2%)'
  },
  {
    name: 'mint',
    prettyName: 'Polar Mint',
    shopifyTitle: 'Polar Mint',
    description: 'A deep cooling spearmint and peppermint flavour.',
    isDark: true,
    active: false,
    background: 'linear-gradient(110.85deg, #5D9892 -7.61%, #0B3C37 115.55%)'
  },
  {
    name: 'berry',
    prettyName: 'Berry',
    shopifyTitle: 'Berry',
    description: 'A deep yet light blend of dark cherry and red fruit notes.',
    isDark: true,
    active: false,
    background: 'linear-gradient(110.85deg, #B15B6B -7.61%, #8D2E41 115.55%)'
  },
  {
    name: 'smooth-tobacco',
    prettyName: 'Smooth Tobacco',
    shopifyTitle: 'Smooth Tobacco',
    description: 'Mellow and subtle flavour.',
    isDark: true,
    active: false,
    background: 'linear-gradient(113.97deg, #D6AB71 -25.73%, #492A21 110.6%)'
  },
  {
    name: 'aromatic-tobacco',
    prettyName: 'Aromatic Tobacco',
    shopifyTitle: 'Aromatic Tobacco',
    description: 'Creamy with light aromatic notes.',
    isDark: true,
    active: false,
    background: 'linear-gradient(113.23deg, #B69552 -13.34%, #362811 116.6%)'
  },
  {
    name: 'smooth-mint',
    prettyName: 'Smooth Mint',
    shopifyTitle: 'Smooth Mint',
    description: 'A subtle and silky cooling mint.',
    isDark: true,
    active: false,
    background: 'linear-gradient(111.48deg, #86C9B8 -20.31%, #3F886C 105.96%)'
  },
  {
    name: 'lemon-berry',
    prettyName: 'Lemon Berry',
    shopifyTitle: 'Lemon Berry',
    description: 'A blend of lemon, citrus and pink grapefruit notes, highlighted by hints of berries.',
    isDark: true,
    active: false,
    background: 'linear-gradient(110.67deg, #FFB3F7 -43.97%, #FFE344 111.93%)'
  },
  {
    name: 'peach',
    prettyName: 'Peach',
    shopifyTitle: 'Peach',
    description: 'White peach with a hint of delicate floral notes.',
    isDark: true,
    active: false,
    background: 'linear-gradient(108.77deg, #E9684D -7.61%, #BB321B 113.2%)'
  },
  {
    name: 'clear',
    prettyName: 'Clear',
    shopifyTitle: 'Clear',
    description: 'Introducing Vuse clear, a unique flavour free experience to discover for ePod.',
    isDark: true,
    active: false,
    background: 'linear-gradient(115.99deg, #DCDCDC -7.61%, #CCCCCC 113.2%)'
  }
]

const pluginWrapper = () => {
  /* eslint-disable no-undef */
  require('fullpage.js/vendors/scrolloverflow.min.js')
  require('scripts/fullpage.fadingEffect.min.js')
  /* eslint-enable no-undef */
}

const EXCLUDED_HANDLES = ['citrus-gin', 'pineapple-melon']

const TrialSlideshow = () => {
  const data = useStaticQuery(graphql`
    query TrialSlideshowQuery {
      allShopifyCollection(filter: { handle: { in: ["epod-2"] } }) {
        nodes {
          products {
            id
            title
            variants {
              availableForSale
              id
              image {
                originalSrc
              }
              price
              selectedOptions {
                name
                value
              }
              sku
              title
            }
          }
        }
      }
      shopifyCollection(handle: { eq: "vype-epod-vpro-cartridges" }) {
        products {
          id
          title
          handle
          variants {
            availableForSale
            id
            image {
              originalSrc
            }
            price
            selectedOptions {
              name
              value
            }
            sku
            title
          }
        }
      }
    }
  `)

  const { addLineItemsToCart, products } = useContext(CheckoutContext)
  const { digitalDataPopulated } = useDigitalData()
  const { province } = useContext(NativeAuthContext)
  const [colors, setColors] = useState(COLORS)
  const [flavours, setFlavours] = useState(FLAVOURS)
  const [state, setState] = useState(null)
  const shopifyDevices = data.allShopifyCollection.nodes[0].products
  const shopifyPods = data.shopifyCollection

  useEffect(() => {
    if (!digitalDataPopulated) return
    const ePenDeviceVariant = shopifyDevices[0].variants[0]
    const splitProductId = ePenDeviceVariant.id.split('__')
    const productId = splitProductId[splitProductId.length - 1]

    const attributes = getVariantAttributes({ variant: ePenDeviceVariant })
    window._satellite.track('prodView', {
      productInfo: [
        {
          productId,
          productName: shopifyDevices[0].title,
          price: ePenDeviceVariant.price,
          productSku: ePenDeviceVariant.sku,
          attributes
        }
      ]
    })
  }, [digitalDataPopulated])

  const handleColorSelect = index => {
    const upToDateColors = [...colors]

    upToDateColors.forEach((color, i) => {
      upToDateColors[i] = { ...color, active: false }
    })

    upToDateColors[index] = { ...upToDateColors[index], active: true }
    setColors([...upToDateColors])
  }

  const handleFlavourSelect = index => {
    const upToDateFlavours = [...flavours]

    upToDateFlavours.forEach((flavour, i) => {
      upToDateFlavours[i] = { ...flavour, active: false }
    })

    upToDateFlavours[index] = { ...upToDateFlavours[index], active: true }
    setFlavours([...upToDateFlavours])
  }

  useEffect(() => {
    if (!products.length) return

    window.fullpage_api.setAllowScrolling(false)
    window.fullpage_api.setKeyboardScrolling(false)

    fetchEpod()
    fetchPods()
  }, [products])

  const handleVariantSelect = ({ currentFlavour, variantIndex }) => {
    const upToDateFlavours = [...flavours]

    const currentFlavourIndex = upToDateFlavours.findIndex(
      flavour => flavour.shopifyTitle === currentFlavour.shopifyTitle
    )
    upToDateFlavours[currentFlavourIndex].variants.forEach(variant => {
      variant.selected = false
    })

    upToDateFlavours[currentFlavourIndex].variants[variantIndex].selected = true
    setFlavours(upToDateFlavours)
  }

  const filteredPods = shopifyPods.products.filter(product => !EXCLUDED_HANDLES.includes(product.handle))

  const fetchEpod = async () => {
    const upToDateColors = shopifyDevices.map(device => {
      let shopifyId = device.id
      if (device.id.includes('Shopify__Product')) {
        const splitId = shopifyId.split('__')
        shopifyId = splitId[splitId.length - 1]
      }
      return {
        ...colors.find(color => color.shopifyTitle === device.title),
        availableForSale: device.variants[0].availableForSale, // need to look into this more, no quantity on the products at the moment..
        variant: device.variants[0],
        title: device.title
      }
    })

    setColors(upToDateColors)
  }

  const fetchPods = async () => {
    let upToDateFlavours = filteredPods.map((product, i) => ({
      ...flavours.find(flavour => flavour.shopifyTitle === product.title),
      active: i === 0,
      variants: product.variants.map((variant, i) => ({
        ...variant,
        selected: i === 0
      })),
      id: product.id
    }))

    upToDateFlavours = upToDateFlavours.filter(flavour => flavour.name)
    setFlavours(upToDateFlavours)
  }

  const currentColor = colors.find(color => color.active)
  const currentFlavour = flavours.find(flavour => flavour.active)

  const currentFlavourVariant = currentFlavour.variants?.find(f => f.selected)
  const fetchedCurrentFlavourVariant = currentFlavourVariant
    ? products
        .map(p => p.variants)
        .flat()
        .find(v => currentFlavourVariant.id.includes(v.shopifyGid))
    : null

  return (
    <>
      <Portal selector="misc">
        <FixedHeader>
          <Header transparent={currentColor.isDark} />
        </FixedHeader>
      </Portal>
      <Container>
        <Details isDark={currentColor.isDark} route="/buy-online/epod-devices" hidePrice>
          <MenuWrap>
            <SlideshowMenu
              isDark={currentColor.isDark}
              color={currentColor.isDark ? white : black}
              items={[
                {
                  label: 'Choose your device colour',
                  anchor: 'step-2',
                  disabled: false
                },
                {
                  label: 'Choose your Pack of Pods',
                  anchor: 'step-3',
                  disabled: !state
                },
                {
                  label: 'View your kit',
                  anchor: 'step-4',
                  disabled: !state || state === 'color-chosen'
                }
              ]}
            />
          </MenuWrap>
        </Details>
        <ScrollConsumer>
          {({ goToAnchor, handleCategoryChange }) => {
            return (
              <ReactFullpage
                licenseKey="AFAC473E-FFB74E20-B48212C5-37B5CDB8"
                fadingEffect
                fadingEffectKey={process.env.GATSBY_FULLPAGE_FADING_EFFECT_KEY}
                scrollOverflow={true}
                pluginWrapper={pluginWrapper}
                onLeave={(origin, destination) => {
                  const anchor = `#${destination.anchor}`
                  goToAnchor(anchor)
                  handleCategoryChange(anchor)
                }}
                render={({ fullpageApi }) => {
                  return (
                    <ReactFullpage.Wrapper>
                      <Section className="section" data-anchor="step-1">
                        <div
                          css={`
                            display: flex;
                            flex-direction: column;
                            height: 100%;
                            width: 100%;
                          `}
                        >
                          <HealthWarning />

                          <Portal selector="misc">
                            <BackButton onClick={() => fullpageApi.moveSectionUp()}>Back</BackButton>
                          </Portal>

                          <div
                            css={`
                              width: 100%;
                              position: relative;
                              flex: 1;
                              background: linear-gradient(101.74deg, #d14441 -7.61%, #f19285 115.55%), #f2968a;
                            `}
                          >
                            <Step1HeaderWrap>
                              <Header transparent />
                            </Step1HeaderWrap>
                            <Step1Content>
                              <Step1Inner
                                css={`
                                  z-index: 100;
                                `}
                              >
                                <Step1Figure>
                                  <img src={`${process.env.BASE_PATH}/images/trial/intro.png`} />
                                </Step1Figure>
                                <Step1Main>
                                  <Step1Heading>
                                    Your Colour. <br />
                                    Your Flavour.
                                  </Step1Heading>
                                  <Step1Subheading>
                                    Customize your own starter kit by selecting <span>1 device</span> and 1 pack of pods
                                    for only $14.99
                                  </Step1Subheading>
                                  <Button
                                    theme="custom"
                                    customColor={white}
                                    variant="outline"
                                    size="large"
                                    onClick={() => fullpageApi.moveSectionDown()}
                                  >
                                    Start customizing
                                  </Button>
                                </Step1Main>
                              </Step1Inner>
                            </Step1Content>

                            <Step1Shard
                              className="u-hide-medium-up"
                              src={`${process.env.BASE_PATH}/images/trial/shard-mobile.png`}
                            />
                            <Step1Shard
                              className="u-hide-medium-down"
                              src={`${process.env.BASE_PATH}/images/trial/shard.png`}
                            />
                          </div>
                        </div>
                      </Section>

                      {/* Step 2 */}
                      <Section className="section" data-anchor="step-2">
                        <Portal selector="misc">
                          <Step2DeviceWrap className="u-hide-medium-down">
                            <img
                              src={`${
                                process.env.BASE_PATH
                              }/images/products/epod2/device-${currentColor.name.toLowerCase()}.png`}
                              alt="Vuse ePod Starter Kit"
                            />
                          </Step2DeviceWrap>
                        </Portal>

                        <Step2Content>
                          <Eyebrow isDark={currentColor.isDark}>01. Choose your device colour</Eyebrow>
                          <Step2Heading isDark={currentColor.isDark}>
                            <span>{currentColor.prettyName}</span> ePod 2
                          </Step2Heading>
                          <Step2MobileDevice
                            src={`${
                              process.env.BASE_PATH
                            }/images/products/epod2/device-${currentColor.name.toLowerCase()}.png`}
                            className="u-hide-medium-up"
                            alt="Vuse ePod Starter Kit"
                          />
                          <Step2SwatchWrap>
                            <SwatchList onClick={handleColorSelect} swatches={colors} isDark={currentColor.isDark} />
                          </Step2SwatchWrap>
                          <Button
                            theme="custom"
                            customColor={currentColor.isDark ? white : black}
                            size="large"
                            variant="outline"
                            onClick={() => {
                              fullpageApi.moveSectionDown()
                              setState('color-chosen')
                            }}
                          >
                            Choose this colour
                          </Button>
                        </Step2Content>

                        <Step2Background style={{ backgroundImage: currentColor.background }} />
                      </Section>

                      {/* Step 3 */}
                      <Section className="section" data-anchor="step-3">
                        <Portal selector="misc">
                          <Step3Figure className="u-hide-medium-down">
                            <img
                              src={`${process.env.BASE_PATH}/images/home/offers/flavours/${currentFlavour.name}.png`}
                            />
                          </Step3Figure>
                        </Portal>
                        <Step3Content>
                          <Eyebrow isDark={currentColor.isDark}>02. Choose your pack of pods</Eyebrow>
                          <Step3Heading
                            isDark={currentColor.isDark}
                            css={`
                              text-transform: uppercase;
                              margin-bottom: 0.25em;
                            `}
                          >
                            {currentFlavour.prettyName}
                          </Step3Heading>
                          <Step3Subheading className="u-hide-medium-down" isDark={currentColor.isDark}>
                            {currentFlavour.description}
                          </Step3Subheading>

                          <Step3MobileFigure className="u-hide-medium-up">
                            <img
                              src={`${process.env.BASE_PATH}/images/home/offers/flavours/${currentFlavour.name}.png`}
                            />
                          </Step3MobileFigure>

                          <Step3FlavourWrap>
                            <FlavourList
                              onClick={handleFlavourSelect}
                              flavours={flavours}
                              isDark={currentColor.isDark}
                            />
                          </Step3FlavourWrap>

                          <Subheading
                            css={`
                              margin-bottom: ${spacing.small};
                            `}
                            isDark={currentColor.isDark}
                          >
                            Choose Nicotine Strength (w/w)
                          </Subheading>

                          <Strengths>
                            <StrengthsInner>
                              {currentFlavour.variants?.map((variant, i) => {
                                const fetchedVariant = products
                                  .map(p => p.variants)
                                  .flat()
                                  .find(v => variant.id.includes(v.shopifyGid))

                                if (fetchedVariant && !fetchedVariant?.availableProvinces.includes(province))
                                  return null

                                const isAvailableForSale = availableForSale({ variant: fetchedVariant, province })

                                const StrengthComponent = () => (
                                  <Strength
                                    key={i}
                                    selected={variant.selected}
                                    onClick={() => {
                                      handleVariantSelect({ currentFlavour, variantIndex: i })
                                    }}
                                    disabled={!isAvailableForSale}
                                  >
                                    {variant.title}
                                  </Strength>
                                )

                                if (!isAvailableForSale)
                                  return (
                                    <Tooltip content={<p style={{ color: 'white' }}>Unavailable</p>}>
                                      <StrengthComponent />
                                    </Tooltip>
                                  )

                                return <StrengthComponent key={i} />
                              })}
                            </StrengthsInner>
                          </Strengths>

                          <Button
                            theme="custom"
                            customColor={currentColor.isDark ? white : black}
                            variant="outline"
                            size="large"
                            onClick={() => {
                              fullpageApi.moveSectionDown()
                              setState('flavour-chosen')
                            }}
                            disabled={!availableForSale({ variant: fetchedCurrentFlavourVariant, province })}
                          >
                            {availableForSale({ variant: fetchedCurrentFlavourVariant, province })
                              ? 'Choose pod pack'
                              : 'Unavailable'}
                          </Button>
                        </Step3Content>

                        <Step3Background style={{ backgroundImage: currentFlavour.background }} />
                      </Section>

                      {/* Step 4 */}
                      <Section className="section" data-anchor="step-4">
                        <Step4Content isDark={currentColor.isDark}>
                          <Heading
                            css={`
                              margin-bottom: 0.25em;
                            `}
                            isDark={currentColor.isDark}
                          >
                            THIS IS YOUR <br />
                            STARTER KIT
                          </Heading>
                          <Step4MobileFigure className="u-hide-medium-up">
                            <img
                              src={`${process.env.BASE_PATH}/images/home/offers/flavours/${currentFlavour.name}.png`}
                            />
                            <img
                              src={`${
                                process.env.BASE_PATH
                              }/images/products/epod2/device-${currentColor.name.toLowerCase()}.png`}
                            />
                          </Step4MobileFigure>
                          <Step4Subheading isDark={currentColor.isDark}>
                            Your choice of ePod 2 {currentColor.prettyName} and {currentFlavour.prettyName} pack is
                            saving you 40%.
                          </Step4Subheading>
                          <Subheading isDark={currentColor.isDark}>
                            <strong>$14.99</strong>
                          </Subheading>
                          <Button
                            theme="custom"
                            customColor={currentColor.isDark ? white : black}
                            variant="outline"
                            onClick={async () => {
                              const selectedFlavourVariant = currentFlavour.variants.find(variant => variant.selected)
                              const lineItems = [
                                {
                                  variantId: currentColor.variant.id,
                                  quantity: 1,
                                  variant: currentColor.variant,
                                  product: currentColor.variant,
                                  customAttributes: [
                                    {
                                      key: 'trial2.0',
                                      value: 'true'
                                    }
                                  ],
                                  title: currentColor.title
                                },
                                {
                                  variantId: selectedFlavourVariant.id,
                                  quantity: 1,
                                  variant: selectedFlavourVariant,
                                  product: selectedFlavourVariant,
                                  customAttributes: [
                                    {
                                      key: 'trial2.0',
                                      value: 'true'
                                    }
                                  ],
                                  title: `${selectedFlavourVariant.title}`
                                }
                              ]

                              await addLineItemsToCart(lineItems, { silent: true })
                              replaceRoute('/cart')
                            }}
                          >
                            Proceed to checkout
                          </Button>
                          <Step4Disclaimer>
                            *Offer valid on one combination of an ePod SOLO device and one pack of ePod pods purchased
                            on vuse.com till June 30, 2021 23:59 EST. Discount applied at checkout. Conditions and
                            restrictions apply. Valid while quantities last. Not for Quebec and Nova Scotia residents.
                            This offer cannot be combined with any other promotions. A valid government-issued ID is
                            required at delivery.
                          </Step4Disclaimer>
                        </Step4Content>
                        <Step3Background style={{ backgroundImage: currentFlavour.background }} />

                        <Portal selector="misc">
                          <Step4Figure className="u-hide-medium-down">
                            <img
                              src={`${process.env.BASE_PATH}/images/home/offers/flavours/${currentFlavour.name}.png`}
                            />
                            <img
                              src={`${
                                process.env.BASE_PATH
                              }/images/products/epod2/device-${currentColor.name.toLowerCase()}.png`}
                            />
                          </Step4Figure>
                        </Portal>
                      </Section>
                    </ReactFullpage.Wrapper>
                  )
                }}
              />
            )
          }}
        </ScrollConsumer>
      </Container>
    </>
  )
}

export default TrialSlideshow
